import React from "react";
import { useLocation } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Button from "./ui/Button";

interface LocationState {
    cardName?: string;
    cardEmail?: string;
}

const OrderConfirmation: React.FC = () => {
    const location = useLocation();
    const state = location.state as LocationState | null;
    const cardName = state?.cardName || 'your card';

    return (
        <div className="bg-white text-coffee-900 dark:text-cream-50 min-h-screen">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 sm:py-28">

                {/* Header */}
                <div className="text-center mb-10">
                    <div className="w-14 h-14 bg-emerald-50 border border-emerald-200 rounded-full mx-auto mb-5 flex items-center justify-center">
                        <Check className="w-7 h-7 text-emerald-700" strokeWidth={3} />
                    </div>
                    <div className="eyebrow mb-3">Order confirmed</div>
                    <h1 className="text-display text-3xl sm:text-5xl text-coffee-900 dark:text-cream-50">
                        Your card is on the way.
                    </h1>
                    <p className="mt-3 text-coffee-600 dark:text-cream-200 text-lg">
                        Request submitted for <strong className="text-coffee-900 dark:text-cream-50">{cardName}</strong>.
                    </p>
                </div>

                {/* What's next */}
                <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl shadow-coffee p-7 sm:p-10 mb-6">
                    <div className="eyebrow mb-3">What's next</div>
                    <h2 className="text-display text-2xl text-coffee-900 dark:text-cream-50 mb-7">
                        Three steps to your door.
                    </h2>
                    <div className="space-y-6">
                        {[
                            {
                                n: "01",
                                title: "Review and approval",
                                body: "We review your card design within 1–2 business days. You'll get an email when it's approved.",
                                accent: false,
                            },
                            {
                                n: "02",
                                title: "Production",
                                body: "Your NFC card is produced with your custom design. This usually takes 3–5 business days.",
                                accent: false,
                            },
                            {
                                n: "03",
                                title: "Shipping",
                                body: "Your card ships to your address. Standard shipping is 5–7 business days within the US.",
                                accent: false,
                            },
                        ].map((s) => (
                            <div key={s.n} className="flex items-start gap-4">
                                <div className="w-9 h-9 rounded-lg bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 flex items-center justify-center flex-shrink-0">
                                    <span className="text-[11px] font-mono text-coffee-500 dark:text-cream-300 font-semibold">{s.n}</span>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-coffee-900 dark:text-cream-50">{s.title}</h3>
                                    <p className="mt-0.5 text-sm text-coffee-600 dark:text-cream-200 leading-relaxed">{s.body}</p>
                                </div>
                            </div>
                        ))}

                        {/* Live now */}
                        <div className="flex items-start gap-4 pt-4 border-t border-coffee-100 dark:border-coffee-800">
                            <div className="w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                                <Check className="w-4 h-4 text-emerald-700" strokeWidth={3} />
                            </div>
                            <div>
                                <h3 className="font-semibold text-coffee-900 dark:text-cream-50">
                                    Your digital card is live now
                                </h3>
                                <p className="mt-0.5 text-sm text-coffee-600 dark:text-cream-200 leading-relaxed">
                                    While you wait for the physical card, your digital card is already active.
                                    Share the link and add custom links any time.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Delivery estimate */}
                <div className="bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-2xl p-6 mb-8 text-center">
                    <div className="eyebrow text-coffee-500 dark:text-cream-300 mb-2">Estimated delivery</div>
                    <p className="text-3xl font-mono font-bold text-coffee-900 dark:text-cream-50">10–14</p>
                    <p className="text-sm text-coffee-600 dark:text-cream-200 mt-1">business days from approval to your doorstep</p>
                </div>

                {/* CTAs */}
                <div className="space-y-3">
                    <Button
                        to="/my-card"
                        variant="primary"
                        fullWidth
                        size="lg"
                        trailingIcon={<ArrowRight className="w-4 h-4" />}
                    >
                        Open my card
                    </Button>
                    <Button to="/order-card" variant="secondary" fullWidth size="lg">
                        Order another card
                    </Button>
                    <Button to="/" variant="ghost" fullWidth>
                        Back to home
                    </Button>
                </div>

            </div>
        </div>
    );
};

export default OrderConfirmation;
